import React, { useEffect, useMemo, useState } from "react";
import {
  Paperclip,
  X,
  FileText,
  Image as ImageIcon,
  Plus,
  ExternalLink,
} from "lucide-react";
import YmdDateInput from "../ui/YmdDateInput";
import CustomSelect from "../ui/CustomSelect";
import Toast from "../ui/Toast";
import {
  getDisconnectRequest,
  updateDisconnectRequest,
  listProviderCircuits,
  fetchEvidenceBlob,
  deleteEvidence,
  DISCONNECT_REASONS,
  DISCONNECT_STATUS,
  statusLabel,
  statusStepIndex,
} from "../../services/disconnectService";

const ACCEPTED_EXTENSIONS = "*/*";

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileKindLabel(file) {
  const name = (file?.name || file?.original_filename || "").toLowerCase();
  if (/\.(png|jpe?g|gif|webp|bmp|tiff?)$/.test(name)) return "Image";
  if (/\.pdf$/.test(name)) return "PDF";
  if (/\.(eml|msg)$/.test(name)) return "Email";
  if (/\.(docx?|rtf)$/.test(name)) return "Word";
  if (/\.txt$/.test(name)) return "Text";
  return "File";
}

function ymd(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function DisconnectStepper({ status }) {
  const step = statusStepIndex(status);
  const fullyComplete = status === DISCONNECT_STATUS.BILLING_STOPPED;
  const steps = [
    { id: 1, label: "Submitted" },
    { id: 2, label: "Confirmed by supplier" },
    { id: 3, label: "Billing stopped" },
  ];
  return (
    <div className="flex items-center gap-2 text-[13px] flex-wrap">
      {steps.map((s, idx) => {
        // When billing stopped, mark step 3 complete (green), not active (red)
        const done = step > s.id || (fullyComplete && s.id === 3);
        const active = step === s.id && !fullyComplete;
        return (
          <React.Fragment key={s.id}>
            {idx > 0 && (
              <div className="w-8 h-px bg-[var(--line,#e4e2dc)] hidden sm:block" />
            )}
            <div className="flex items-center gap-2">
              <span
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold ${
                  done
                    ? "bg-[var(--good,#1f6b34)] text-white"
                    : active
                      ? "bg-[var(--accent,#a51c1c)] text-white"
                      : "bg-[var(--sunken,#eeece7)] text-[var(--muted,#6b6a66)]"
                }`}
              >
                {done ? "✓" : s.id}
              </span>
              <span
                className={
                  active
                    ? "font-semibold text-[var(--accent,#a51c1c)]"
                    : done
                      ? "text-[var(--good,#1f6b34)]"
                      : "text-[var(--muted,#6b6a66)]"
                }
              >
                {s.label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function StatusPill({ status }) {
  let cls = "bg-sky-100 text-sky-900 border-sky-200";
  if (status === DISCONNECT_STATUS.CONFIRMED) {
    cls = "bg-amber-100 text-amber-900 border-amber-200";
  } else if (status === DISCONNECT_STATUS.BILLING_STOPPED) {
    cls = "bg-emerald-100 text-emerald-900 border-emerald-200";
  }
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls}`}
    >
      {statusLabel(status)}
    </span>
  );
}

/** Prefer saved group status (e.g. billing_stopped); else derive from tracking. */
function groupDisplayStatus(g) {
  if (g?.status === DISCONNECT_STATUS.BILLING_STOPPED) {
    return DISCONNECT_STATUS.BILLING_STOPPED;
  }
  if (String(g?.tracking_number || "").trim()) {
    return DISCONNECT_STATUS.CONFIRMED;
  }
  if (g?.status === DISCONNECT_STATUS.CONFIRMED) {
    return DISCONNECT_STATUS.CONFIRMED;
  }
  return DISCONNECT_STATUS.SUBMITTED;
}

let tempGroupSeq = 1;
function nextTempGroupId() {
  return `new-${tempGroupSeq++}`;
}

/**
 * View / edit an existing disconnect request.
 * Supports supplier order groups: each group has its own tracking number and
 * supplier dates. Create a new group to split circuits by different tracking numbers.
 */
const DisconnectRequestDetailModal = ({
  requestId,
  initialData = null,
  tenantId,
  readOnly = false,
  onClose,
  onSaved,
}) => {
  const [loading, setLoading] = useState(!initialData);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({
    title: "",
    message: null,
    type: "success",
  });
  const showErrorToast = (title, message) => {
    setToast({
      title: title || "Error",
      message: message || "Something went wrong",
      type: "error",
    });
  };
  const [provider, setProvider] = useState("");
  const [requestNumber, setRequestNumber] = useState("");
  const [status, setStatus] = useState(DISCONNECT_STATUS.SUBMITTED);
  const [groups, setGroups] = useState([]);
  const [requestedBy, setRequestedBy] = useState("Hilton Sturisky");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [evidence, setEvidence] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [pendingDeleteEvidenceIds, setPendingDeleteEvidenceIds] = useState([]);
  const [showAddCircuits, setShowAddCircuits] = useState(false);
  const [addToGroupKey, setAddToGroupKey] = useState(null);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerRows, setPickerRows] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [selectedPickerIds, setSelectedPickerIds] = useState(() => new Set());
  const [pendingAddCircuits, setPendingAddCircuits] = useState([]);

  const hydrate = (data) => {
    if (!data) return;
    setProvider(data.provider || "");
    setRequestNumber(data.request_number || "");
    setStatus(data.status || DISCONNECT_STATUS.SUBMITTED);
    setRequestedBy(data.requested_by || "Hilton Sturisky");
    setReason(data.reason || "");
    setNotes(data.notes || "");
    setEvidence(Array.isArray(data.evidence) ? data.evidence : []);
    setPendingDeleteEvidenceIds([]);
    const gList = (data.supplier_groups || []).map((g) => ({
      key: String(g.id),
      id: g.id,
      temp_id: null,
      date_submitted_to_supplier: ymd(g.date_submitted_to_supplier),
      tracking_number: g.tracking_number || "",
      supplier_contact: g.supplier_contact || "",
      supplier_confirmed_disconnect_date: ymd(
        g.supplier_confirmed_disconnect_date
      ),
      last_expected_bill_date: ymd(g.last_expected_bill_date),
      status: g.status,
      circuits: (g.circuits || []).map((c) => ({
        id: c.id,
        inventory_id: c.inventory_id,
        circuit_id: c.circuit_id,
        address: c.address || "",
        service_type: c.service_type || "",
        billing_account: c.billing_account || "",
        mrc: c.mrc != null ? Number(c.mrc) : null,
      })),
    }));
    if (gList.length > 0) {
      setGroups(gList);
    } else {
      const temp = nextTempGroupId();
      setGroups([
        {
          key: temp,
          id: null,
          temp_id: temp,
          date_submitted_to_supplier: "",
          tracking_number: "",
          supplier_contact: "",
          supplier_confirmed_disconnect_date: "",
          last_expected_bill_date: "",
          status: DISCONNECT_STATUS.SUBMITTED,
          circuits: [],
        },
      ]);
    }
    setPendingAddCircuits([]);
    setNewFiles([]);
  };

  useEffect(() => {
    if (initialData) {
      hydrate(initialData);
      setLoading(false);
      return;
    }
    if (!requestId || !tenantId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getDisconnectRequest(requestId, tenantId);
        if (!cancelled) hydrate(res?.data);
      } catch (err) {
        if (!cancelled) {
          showErrorToast(
            "Load failed",
            err.message || "Failed to load request"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [requestId, tenantId, initialData]);

  const allCircuits = useMemo(
    () => groups.flatMap((g) => g.circuits || []),
    [groups]
  );
  const totalMrc = allCircuits.reduce((s, c) => s + (Number(c.mrc) || 0), 0);

  const derivedStatus = useMemo(() => {
    const hasTracking = groups.some((g) => String(g.tracking_number || "").trim());
    if (status === DISCONNECT_STATUS.BILLING_STOPPED) {
      return DISCONNECT_STATUS.BILLING_STOPPED;
    }
    return hasTracking
      ? DISCONNECT_STATUS.CONFIRMED
      : DISCONNECT_STATUS.SUBMITTED;
  }, [groups, status]);

  const fieldClass =
    "w-full rounded-md border border-[var(--line,#e4e2dc)] bg-[var(--sunken,#eeece7)] px-3 py-2 text-[13px] text-[var(--ink,#1c1c1a)] placeholder:text-[var(--muted,#6b6a66)] focus:outline-none focus:ring-1 focus:ring-[var(--accent,#a51c1c)] disabled:opacity-70";

  const updateGroup = (key, patch) => {
    setGroups((prev) =>
      prev.map((g) => (g.key === key ? { ...g, ...patch } : g))
    );
  };

  const addSupplierOrderGroup = () => {
    const temp = nextTempGroupId();
    setGroups((prev) => [
      ...prev,
      {
        key: temp,
        id: null,
        temp_id: temp, // same id as key for new groups
        date_submitted_to_supplier: "",
        tracking_number: "",
        supplier_contact: "",
        supplier_confirmed_disconnect_date: "",
        last_expected_bill_date: "",
        status: DISCONNECT_STATUS.SUBMITTED,
        circuits: [],
      },
    ]);
  };

  const moveCircuitToGroup = (circuitId, fromKey, toKey) => {
    if (!toKey || fromKey === toKey) return;
    setGroups((prev) => {
      let moved = null;
      const without = prev.map((g) => {
        if (g.key !== fromKey) return g;
        const nextCircuits = (g.circuits || []).filter((c) => {
          if (c.circuit_id === circuitId) {
            moved = c;
            return false;
          }
          return true;
        });
        return { ...g, circuits: nextCircuits };
      });
      if (!moved) return prev;
      return without.map((g) =>
        g.key === toKey
          ? { ...g, circuits: [...(g.circuits || []), moved] }
          : g
      );
    });
  };

  const openAddCircuits = (groupKey) => {
    setAddToGroupKey(groupKey);
    setSelectedPickerIds(new Set());
    setPickerSearch("");
    setShowAddCircuits(true);
  };

  useEffect(() => {
    if (!showAddCircuits || !provider || !tenantId) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setPickerLoading(true);
      try {
        const res = await listProviderCircuits(tenantId, provider, {
          search: pickerSearch,
          excludeRequestId: requestId,
        });
        if (cancelled) return;
        const existing = new Set(
          [
            ...allCircuits.map((c) => String(c.circuit_id || "").toLowerCase()),
            ...pendingAddCircuits.map((c) =>
              String(c.circuit_id || "").toLowerCase()
            ),
          ]
        );
        setPickerRows(
          (res?.data || []).filter(
            (r) => !existing.has(String(r.circuit_id || "").toLowerCase())
          )
        );
      } catch {
        if (!cancelled) setPickerRows([]);
      } finally {
        if (!cancelled) setPickerLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [
    showAddCircuits,
    provider,
    tenantId,
    pickerSearch,
    allCircuits,
    pendingAddCircuits,
    requestId,
  ]);

  const confirmAddCircuits = () => {
    const toAdd = pickerRows
      .filter((r) => selectedPickerIds.has(r.inventory_id))
      .map((r) => ({
        inventory_id: r.inventory_id,
        circuit_id: r.circuit_id,
        address: r.address || "",
        service_type: r.service_type || "",
        billing_account: r.billing_account || "",
        mrc: r.mrc != null ? Number(r.mrc) : null,
        _new: true,
      }));
    setGroups((prev) =>
      prev.map((g) =>
        g.key === addToGroupKey
          ? { ...g, circuits: [...(g.circuits || []), ...toAdd] }
          : g
      )
    );
    setPendingAddCircuits((prev) => [
      ...prev,
      ...toAdd.map((c) => ({ ...c, groupKey: addToGroupKey })),
    ]);
    setShowAddCircuits(false);
  };

  const openEvidence = async (ev) => {
    try {
      const blob = await fetchEvidenceBlob(requestId, ev.id, tenantId);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      showErrorToast("Unable to open file", err.message || "Unable to open file");
    }
  };

  const removeEvidence = (ev) => {
    if (readOnly) return;
    // Defer API delete until Save Changes; X only hides from the UI
    setPendingDeleteEvidenceIds((prev) =>
      prev.includes(ev.id) ? prev : [...prev, ev.id]
    );
    setEvidence((prev) => prev.filter((e) => e.id !== ev.id));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (readOnly) return;

    // Tracking number requires Last Expected Bill Date (needed for billing stopped)
    const missingLeb = groups.find(
      (g) =>
        (g.circuits || []).length > 0 &&
        String(g.tracking_number || "").trim() &&
        !String(g.last_expected_bill_date || "").trim()
    );
    if (missingLeb) {
      const idx = groups.findIndex((g) => g.key === missingLeb.key) + 1;
      setToast({
        title: "Cannot save",
        message: `Group ${idx}: Last expected bill date is required when a tracking number is set.`,
        type: "error",
      });
      return;
    }

    setSaving(true);
    try {
      const existingGroups = groups.filter((g) => g.id != null);
      const createdGroups = groups.filter((g) => g.id == null);

      const move_circuits = [];
      for (const g of groups) {
        for (const c of g.circuits || []) {
          if (c._new || c.id == null) continue;
          if (g.id != null) {
            move_circuits.push({
              id: c.id,
              supplier_group_id: g.id,
            });
          } else {
            move_circuits.push({
              id: c.id,
              temp_group_id: g.temp_id || g.key,
            });
          }
        }
      }

      const addByGroup = new Map();
      for (const g of groups) {
        const news = (g.circuits || []).filter((c) => c._new);
        if (news.length === 0) continue;
        addByGroup.set(g.key, { group: g, circuits: news });
      }

      const payload = {
        tenant_id: tenantId,
        requested_by: requestedBy || "Hilton Sturisky",
        reason: reason || null,
        notes: notes || null,
        groups: existingGroups.map((g) => ({
          id: g.id,
          date_submitted_to_supplier: g.date_submitted_to_supplier || null,
          tracking_number: g.tracking_number || null,
          supplier_contact: g.supplier_contact || null,
          supplier_confirmed_disconnect_date:
            g.supplier_confirmed_disconnect_date || null,
          last_expected_bill_date: g.last_expected_bill_date || null,
        })),
        new_groups: createdGroups.map((g) => ({
          temp_id: g.temp_id || g.key,
          date_submitted_to_supplier: g.date_submitted_to_supplier || null,
          tracking_number: g.tracking_number || null,
          supplier_contact: g.supplier_contact || null,
          supplier_confirmed_disconnect_date:
            g.supplier_confirmed_disconnect_date || null,
          last_expected_bill_date: g.last_expected_bill_date || null,
          circuit_ids: (g.circuits || [])
            .filter((c) => c.id != null && !c._new)
            .map((c) => c.id),
        })),
        move_circuits,
      };

      // Delete evidence marked with X only when user saves
      if (pendingDeleteEvidenceIds.length > 0) {
        for (const evidenceId of pendingDeleteEvidenceIds) {
          await deleteEvidence(requestId, evidenceId, tenantId);
        }
        setPendingDeleteEvidenceIds([]);
      }

      // First save groups / moves / new groups (+ evidence files)
      let result = await updateDisconnectRequest(requestId, payload, newFiles);

      // Then add newly picked circuits per target group
      if (addByGroup.size > 0) {
        const returnedGroups = result?.data?.supplier_groups || [];
        for (const [, { group, circuits }] of addByGroup.entries()) {
          let targetId = group.id;
          if (!targetId) {
            const match = returnedGroups.find(
              (rg) =>
                String(rg.tracking_number || "").trim().toLowerCase() ===
                String(group.tracking_number || "").trim().toLowerCase()
            );
            targetId =
              match?.id ||
              returnedGroups[returnedGroups.length - 1]?.id ||
              null;
          }
          if (!targetId) continue;
          result = await updateDisconnectRequest(
            requestId,
            {
              tenant_id: tenantId,
              add_circuits: circuits.map((c) => ({
                inventory_id: c.inventory_id,
                circuit_id: c.circuit_id,
                address: c.address,
                service_type: c.service_type,
                billing_account: c.billing_account,
                mrc: c.mrc,
              })),
              add_to_group_id: targetId,
            },
            []
          );
        }
      }

      hydrate(result?.data);
      setSaving(false);
      // Notify parent first so its toast mounts before this modal unmounts
      if (typeof onSaved === "function") {
        onSaved(result?.data, { saved: true });
      }
      // Defer close so parent toast state paints (avoids toast never appearing)
      window.setTimeout(() => {
        if (typeof onClose === "function") onClose();
      }, 50);
    } catch (err) {
      showErrorToast(
        "Save failed",
        err.message || "Failed to save changes"
      );
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4">
      <Toast
        title={toast.title}
        message={toast.message}
        type={toast.type}
        duration={5000}
        onClose={() =>
          setToast({
            title: "",
            message: null,
            type: "success",
          })
        }
      />
      <div
        className="relative flex max-h-[92vh] w-full max-w-[960px] flex-col overflow-hidden rounded-[12px] border border-[var(--line,#e4e2dc)] bg-white shadow-[0_20px_60px_rgba(28,28,26,0.28)]"
        role="dialog"
        aria-modal="true"
      >
        <form onSubmit={handleSave} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-shrink-0 border-b border-[var(--line,#e4e2dc)] px-6 pt-5 pb-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--accent,#a51c1c)]">
                  Disconnect request
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <h2 className="text-[22px] font-semibold text-[var(--ink,#1c1c1a)]">
                    {requestNumber || "—"}
                  </h2>
                  {provider ? (
                    <span className="inline-flex items-center rounded-full border border-[var(--line,#e4e2dc)] bg-[var(--sunken,#eeece7)] px-2.5 py-0.5 text-[12px] font-medium">
                      {provider}
                    </span>
                  ) : null}
                  <StatusPill status={derivedStatus} />
                </div>
                <p className="mt-1 text-[13px] text-[var(--muted,#6b6a66)]">
                  {allCircuits.length} circuit
                  {allCircuits.length === 1 ? "" : "s"} · {formatMoney(totalMrc)}{" "}
                  MRC being removed
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md p-1.5 text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)]"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-4">
              <DisconnectStepper status={derivedStatus} />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {loading ? (
              <p className="text-[13px] text-[var(--muted,#6b6a66)]">Loading…</p>
            ) : (
              <>
                <section>
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <h3 className="text-[14px] font-semibold text-[var(--ink,#1c1c1a)]">
                        Supplier order groups
                      </h3>
                      <p className="text-[12px] text-[var(--muted,#6b6a66)]">
                        Same tracking number → one group. Different tracking
                        numbers → create a separate group and move circuits into
                        it. Only {provider || "this provider"} circuits can be
                        added.
                      </p>
                    </div>
                    {!readOnly ? (
                      <button
                        type="button"
                        onClick={addSupplierOrderGroup}
                        className="inline-flex items-center gap-1 rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-2.5 py-1.5 text-[12px] font-medium hover:bg-[var(--sunken,#eeece7)]"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add supplier order group
                      </button>
                    ) : null}
                  </div>

                  <div className="space-y-4">
                    {groups.map((g, gi) => (
                      <div
                        key={g.key}
                        className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white overflow-hidden"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--line,#e4e2dc)] bg-[var(--sunken,#eeece7)] px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span className="text-[13px] font-semibold">
                              Group {gi + 1}
                            </span>
                            <StatusPill status={groupDisplayStatus(g)} />
                          </div>
                          {!readOnly ? (
                            <button
                              type="button"
                              onClick={() => openAddCircuits(g.key)}
                              className="inline-flex items-center gap-1 rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-2 py-1 text-[12px] font-medium"
                            >
                              <Plus className="h-3.5 w-3.5" />
                              Add circuits
                            </button>
                          ) : null}
                        </div>

                        <div className="p-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                          <label className="block">
                            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                              Date submitted to supplier
                            </span>
                            <YmdDateInput
                              value={g.date_submitted_to_supplier}
                              onChange={(e) =>
                                updateGroup(g.key, {
                                  date_submitted_to_supplier: e.target.value,
                                })
                              }
                              disabled={readOnly}
                              className={fieldClass}
                            />
                          </label>
                          <label className="block">
                            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                              Supplier tracking or order number
                            </span>
                            <input
                              type="text"
                              value={g.tracking_number}
                              onChange={(e) =>
                                updateGroup(g.key, {
                                  tracking_number: e.target.value,
                                })
                              }
                              disabled={readOnly}
                              placeholder="Order or ticket number"
                              className={fieldClass}
                            />
                          </label>
                          <label className="block">
                            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                              Supplier contact
                            </span>
                            <input
                              type="text"
                              value={g.supplier_contact}
                              onChange={(e) =>
                                updateGroup(g.key, {
                                  supplier_contact: e.target.value,
                                })
                              }
                              disabled={readOnly}
                              placeholder="Name or email"
                              className={fieldClass}
                            />
                          </label>
                          <label className="block">
                            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                              Supplier confirmed disconnect date
                            </span>
                            <YmdDateInput
                              value={g.supplier_confirmed_disconnect_date}
                              onChange={(e) =>
                                updateGroup(g.key, {
                                  supplier_confirmed_disconnect_date:
                                    e.target.value,
                                })
                              }
                              disabled={readOnly}
                              className={fieldClass}
                            />
                          </label>
                          <label className="block">
                            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                              Last expected bill date
                              {String(g.tracking_number || "").trim() ? (
                                <span className="text-[var(--crit,#c0262b)]"> *</span>
                              ) : null}
                            </span>
                            <YmdDateInput
                              value={g.last_expected_bill_date}
                              onChange={(e) =>
                                updateGroup(g.key, {
                                  last_expected_bill_date: e.target.value,
                                })
                              }
                              disabled={readOnly}
                              className={fieldClass}
                              aria-label={
                                String(g.tracking_number || "").trim()
                                  ? "Last expected bill date (required with tracking number)"
                                  : "Last expected bill date"
                              }
                            />
                          </label>
                        </div>

                        <div className="border-t border-[var(--line,#e4e2dc)] overflow-x-auto">
                          <table className="w-full text-left text-[13px]">
                            <thead className="text-[11px] font-semibold uppercase text-[var(--muted,#6b6a66)]">
                              <tr>
                                <th className="px-3 py-2">Circuit ID</th>
                                <th className="px-3 py-2">Address</th>
                                <th className="px-3 py-2">Service type</th>
                                <th className="px-3 py-2 text-right">MRC</th>
                                {!readOnly && groups.length > 1 ? (
                                  <th className="px-3 py-2">Move to group</th>
                                ) : null}
                              </tr>
                            </thead>
                            <tbody>
                              {(g.circuits || []).length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={5}
                                    className="px-3 py-3 text-[12px] text-[var(--muted,#6b6a66)]"
                                  >
                                    No circuits in this group yet. Add circuits
                                    or move some from another group.
                                  </td>
                                </tr>
                              ) : (
                                (g.circuits || []).map((c) => (
                                  <tr
                                    key={`${g.key}-${c.circuit_id}`}
                                    className="border-t border-[var(--line,#e4e2dc)]"
                                  >
                                    <td className="px-3 py-2 font-medium">
                                      {c.circuit_id}
                                      {c._new ? (
                                        <span className="ml-1 text-[11px] text-[var(--accent,#a51c1c)]">
                                          new
                                        </span>
                                      ) : null}
                                    </td>
                                    <td className="px-3 py-2 text-[var(--muted,#6b6a66)]">
                                      {c.address || "—"}
                                    </td>
                                    <td className="px-3 py-2">
                                      {c.service_type || "—"}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">
                                      {formatMoney(c.mrc)}
                                    </td>
                                    {!readOnly && groups.length > 1 ? (
                                      <td className="px-3 py-2 min-w-[140px]">
                                        <CustomSelect
                                          value={g.key}
                                          onChange={(e) =>
                                            moveCircuitToGroup(
                                              c.circuit_id,
                                              g.key,
                                              e?.target?.value ?? ""
                                            )
                                          }
                                          options={groups.map((opt, oi) => ({
                                            value: opt.key,
                                            label: `Group ${oi + 1}${
                                              opt.tracking_number
                                                ? ` (${opt.tracking_number})`
                                                : ""
                                            }`,
                                          }))}
                                          className="!min-h-[32px] py-1 text-[12px] bg-[var(--sunken,#eeece7)]"
                                          aria-label="Move to group"
                                        />
                                      </td>
                                    ) : null}
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="mb-3 text-[14px] font-semibold">Details</h3>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className="block">
                      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                        Requested by
                      </span>
                      <input
                        type="text"
                        value={requestedBy}
                        onChange={(e) => setRequestedBy(e.target.value)}
                        disabled={readOnly}
                        className={fieldClass}
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                        Reason
                      </span>
                      <CustomSelect
                        value={reason}
                        onChange={(e) => setReason(e?.target?.value ?? "")}
                        disabled={readOnly}
                        options={[
                          { value: "", label: "Select a reason" },
                          ...DISCONNECT_REASONS.map((r) => ({
                            value: r,
                            label: r,
                          })),
                        ]}
                        className={fieldClass}
                        placeholder="Select a reason"
                      />
                    </label>
                    <label className="block sm:col-span-2">
                      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
                        Notes
                      </span>
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        disabled={readOnly}
                        rows={3}
                        className={fieldClass}
                      />
                    </label>
                  </div>
                </section>

                <section>
                  <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                    <h3 className="text-[14px] font-semibold">Evidence</h3>
                    <p className="text-[12px] text-[var(--muted,#6b6a66)]">
                      Same evidence applies to all circuits on this request.
                    </p>
                  </div>
                  {!readOnly ? (
                    <label
                      className="flex cursor-pointer items-center gap-3 rounded-[10px] border border-dashed border-[var(--accent,#a51c1c)] bg-[var(--accent-soft,#fbe4e4)]/40 px-4 py-4 hover:bg-[var(--accent-soft,#fbe4e4)]/70"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        setNewFiles((prev) => [
                          ...prev,
                          ...Array.from(e.dataTransfer.files || []),
                        ]);
                      }}
                    >
                      <Paperclip className="h-5 w-5 shrink-0 text-[var(--accent,#a51c1c)]" />
                      <div>
                        <p className="text-[13px] font-medium">
                          Drop files here or click to upload.
                        </p>
                        <p className="text-[12px] text-[var(--muted,#6b6a66)]">
                          Accepts .eml, .msg, PDF, images, Word and text — and
                          any other file.
                        </p>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        multiple
                        accept={ACCEPTED_EXTENSIONS}
                        onChange={(e) => {
                          setNewFiles((prev) => [
                            ...prev,
                            ...Array.from(e.target.files || []),
                          ]);
                          e.target.value = "";
                        }}
                      />
                    </label>
                  ) : null}

                  <ul className="mt-3 space-y-2">
                    {evidence.map((ev) => (
                      <li
                        key={ev.id}
                        className="flex items-center gap-3 rounded-[10px] border border-[var(--line,#e4e2dc)] px-3 py-2"
                      >
                        {fileKindLabel(ev) === "Image" ? (
                          <ImageIcon className="h-5 w-5 text-[var(--muted,#6b6a66)]" />
                        ) : (
                          <FileText className="h-5 w-5 text-[var(--muted,#6b6a66)]" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[13px] font-medium">
                            {ev.original_filename}
                          </p>
                          <p className="text-[11px] text-[var(--muted,#6b6a66)]">
                            {fileKindLabel(ev)}
                            {ev.file_size != null
                              ? ` · ${formatBytes(ev.file_size)}`
                              : ""}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => openEvidence(ev)}
                          className="rounded p-1 hover:bg-[var(--sunken,#eeece7)]"
                          aria-label="Open"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </button>
                        {!readOnly ? (
                          <button
                            type="button"
                            onClick={() => removeEvidence(ev)}
                            className="rounded p-1 hover:bg-[var(--sunken,#eeece7)]"
                            aria-label="Remove"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        ) : null}
                      </li>
                    ))}
                    {newFiles.map((file, index) => (
                      <li
                        key={`new-${file.name}-${index}`}
                        className="flex items-center gap-3 rounded-[10px] border border-[var(--line,#e4e2dc)] px-3 py-2"
                      >
                        <FileText className="h-5 w-5 text-[var(--muted,#6b6a66)]" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[13px] font-medium">
                            {file.name}
                          </p>
                          <p className="text-[11px] text-[var(--muted,#6b6a66)]">
                            Pending upload · {formatBytes(file.size)}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setNewFiles((prev) =>
                              prev.filter((_, i) => i !== index)
                            )
                          }
                          className="rounded p-1 hover:bg-[var(--sunken,#eeece7)]"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                    {evidence.length === 0 && newFiles.length === 0 ? (
                      <p className="text-[12px] text-[var(--muted,#6b6a66)]">
                        No evidence on file yet.
                      </p>
                    ) : null}
                  </ul>
                </section>
              </>
            )}

          </div>

          <div className="flex flex-shrink-0 items-center justify-between gap-3 border-t border-[var(--line,#e4e2dc)] px-6 py-3">
            <p className="text-[12px] text-[var(--muted,#6b6a66)]">
              Saved to each circuit&apos;s history and the Disconnects report.
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-4 py-2 text-[13px] font-medium hover:bg-[var(--sunken,#eeece7)]"
              >
                {readOnly ? "Close" : "Cancel"}
              </button>
              {!readOnly ? (
                <button
                  type="submit"
                  disabled={saving || loading}
                  className="inline-flex items-center justify-center rounded-md bg-[var(--accent,#a51c1c)] px-4 py-2 text-[13px] font-medium text-white hover:opacity-90 disabled:opacity-50"
                >
                  {saving ? "Saving…" : "Save changes"}
                </button>
              ) : null}
            </div>
          </div>
        </form>

        {showAddCircuits ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/30 p-6">
            <div className="flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-[12px] border border-[var(--line,#e4e2dc)] bg-white shadow-lg">
              <div className="flex items-center justify-between border-b border-[var(--line,#e4e2dc)] px-4 py-3">
                <div>
                  <h4 className="text-[14px] font-semibold">
                    Add {provider} circuits
                  </h4>
                  <p className="text-[12px] text-[var(--muted,#6b6a66)]">
                    Other providers are excluded.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAddCircuits(false)}
                  className="rounded p-1 hover:bg-[var(--sunken,#eeece7)]"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="px-4 py-2">
                <input
                  type="search"
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  placeholder="Search circuit, account, or address"
                  className={fieldClass}
                />
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
                {pickerLoading ? (
                  <p className="px-2 py-4 text-[13px] text-[var(--muted,#6b6a66)]">
                    Loading…
                  </p>
                ) : pickerRows.length === 0 ? (
                  <p className="px-2 py-4 text-[13px] text-[var(--muted,#6b6a66)]">
                    No matching circuits.
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {pickerRows.map((r) => (
                      <li key={r.inventory_id}>
                        <label className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-2 hover:bg-[var(--sunken,#eeece7)]">
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={selectedPickerIds.has(r.inventory_id)}
                            onChange={() => {
                              setSelectedPickerIds((prev) => {
                                const next = new Set(prev);
                                if (next.has(r.inventory_id)) {
                                  next.delete(r.inventory_id);
                                } else {
                                  next.add(r.inventory_id);
                                }
                                return next;
                              });
                            }}
                          />
                          <span className="min-w-0">
                            <span className="block text-[13px] font-medium">
                              {r.circuit_id}
                            </span>
                            <span className="block text-[12px] text-[var(--muted,#6b6a66)]">
                              {r.address || "—"} · {formatMoney(r.mrc)}
                            </span>
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="flex justify-end gap-2 border-t border-[var(--line,#e4e2dc)] px-4 py-3">
                <button
                  type="button"
                  onClick={() => setShowAddCircuits(false)}
                  className="rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-3 py-1.5 text-[13px]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={selectedPickerIds.size === 0}
                  onClick={confirmAddCircuits}
                  className="rounded-md bg-[var(--accent,#a51c1c)] px-3 py-1.5 text-[13px] font-medium text-white disabled:opacity-50"
                >
                  Add {selectedPickerIds.size || ""}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default DisconnectRequestDetailModal;
